import { marketIdentityKey } from './sportsnewDetailPatch.js';
import {
  sportsnewListPatchFixtures, mapSportsnewFixtureOdds,
  mergeOddPreservingIdentity, SPORTSNEW_ODD_GROUP_FIELDS, sportsnewOddEvidence,
  compareSportsnewOddEvidence, applySportsnewOddPatchesToMarkets,
  sportsnewDisplayOddGroupEvidence, withSportsnewDisplayOddEvidence,
  normalizeAcceptedSportsnewOddAvailability,
} from './sportsnewListPatch.js';
import { isEntityLocked } from './sportsnewDisplayLifecycle.js';

const MAX_TRACKED_ODDS = 1000;
const MAX_TRACKED_MARKETS = 500;

const oddId = (value) => {
  const id = value?.oddStateId ?? value?.id ?? value;
  return id == null ? null : String(id);
};

const timestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeOddPatch = (patch = {}) => {
  const nextPrice = patch.priceDecimal ?? patch.decimalOdds ?? patch.odds ?? patch.price;
  return {
    ...patch,
    ...(nextPrice != null ? { priceDecimal: nextPrice } : {}),
  };
};

const mergeOddPatchPreservingIdentity = mergeOddPreservingIdentity;

const marketOddsRows = (markets) => (
  (Array.isArray(markets) ? markets : []).flatMap((market) => (
    market?.selections ?? market?.odds ?? []
  ))
);

const marketStateId = (market) => (
  market?.marketStateId ?? market?.market_state_id ?? market?.id ?? market?.market_id ?? null
);

const marketPatchScope = (market) => String(
  market?.marketPatchScope ?? market?.market_patch_scope ?? ''
).trim().toLowerCase();

const sharedMarketStateKey = (market) => {
  const stateId = marketStateId(market);
  return stateId != null ? `shared:${stateId}` : null;
};

const marketLedgerKey = (market) => (
  marketPatchScope(market) === 'shared_market_state'
    ? sharedMarketStateKey(market)
    : marketIdentityKey(market)
);

// Mirrors API display_push_payload.projector MARKET_FRAGMENT_FIELDS. Broad
// shared-state patches carry lifecycle truth only; their synthetic display
// metadata must never replace a sibling fragment's grouping/sort identity.
const MARKET_FRAGMENT_FIELDS = [
  'marketGroupId',
  'baseMarketGroupKey',
  'displaySectionKey',
  'marketTypeKey',
  'marketCategoryKey',
  'displayPrimaryCategoryKey',
  'displayCategoryKeys',
  'displayCategoryLabelKo',
  'displayCategoryOrder',
  'sortKey',
  'line',
  'lineKey',
  'canonicalLineKey',
  'marketFamily',
  'periodScope',
  'teamSide',
  // Frontend fallback identity dimensions beyond the current API constant.
  'points',
  'team',
  'selectionSetKey',
  'selectionSetId',
];

const compareMarketEntries = (left, right) => {
  // The fixture callback already fences its connection/room sequence.
  return Number(left?.revision ?? 0) - Number(right?.revision ?? 0);
};

const marketEntryFor = (ledger, market) => {
  const stateId = marketStateId(market);
  const keys = [
    marketIdentityKey(market),
    marketIdentityKey({ ...market, marketGroupId: null }),
    sharedMarketStateKey(market),
    stateId != null ? marketIdentityKey({ marketStateId: stateId }) : null,
  ];
  return keys
    .filter((key, index) => key != null && keys.indexOf(key) === index)
    .map((key) => ledger.byMarketKey.get(key))
    .filter(Boolean)
    .reduce((newest, entry) => (
      !newest || compareMarketEntries(entry, newest) > 0 ? entry : newest
    ), null);
};

const normalizeMarketLifecyclePatch = (market) => {
  const normalized = { ...market };
  delete normalized.selections;
  delete normalized.odds;
  if (marketPatchScope(market) === 'shared_market_state') {
    MARKET_FRAGMENT_FIELDS.forEach((field) => { delete normalized[field]; });
  }
  return normalized;
};

const isExplicitOpenEntity = (entity) => [entity, entity?.marketState].some((shape) => (
  shape?.bettable === true
  || shape?.isBettable === true
  || shape?.is_bettable === true
  || shape?.suspended === false
  || shape?.isSuspended === false
  || shape?.is_suspended === false
  || shape?.locked === false
  || (typeof shape?.state === 'string' && /^(OPEN|ACTIVE|AVAILABLE|UNLOCKED)$/i.test(shape.state.trim()))
  || (typeof shape?.displayState === 'string' && /^(OPEN|ACTIVE|AVAILABLE|UNLOCKED)$/i.test(shape.displayState.trim()))
));

const oddEvidenceMatchesMarket = (odd, market) => {
  if (odd?.marketGroupId != null && market?.marketGroupId != null) {
    return String(odd.marketGroupId) === String(market.marketGroupId);
  }
  const oddKey = marketIdentityKey(odd);
  const marketKey = marketIdentityKey(market);
  return oddKey != null && marketKey != null && oddKey === marketKey;
};

const hasAuthoritativeOpenEvidence = (market, oddEvidence) => {
  const embedded = marketOddsRows([market]);
  const external = (Array.isArray(oddEvidence) ? oddEvidence : [oddEvidence])
    .filter((odd) => oddEvidenceMatchesMarket(odd, market));
  return [...embedded, ...external].some((entity) => (
    !isEntityLocked(entity) && isExplicitOpenEntity(entity)
  ));
};


export const createSportsnewDetailRealtimeLedger = () => ({
  revision: 0,
  byOddId: new Map(),
  byMarketKey: new Map(),
  commandIds: new Set(),
  commandScope: null,
});

export const createSportsnewDetailRestRevisionTracker = () => new WeakMap();

export const trackSportsnewDetailRestResponse = (tracker, response, requestRevision) => {
  if (!(tracker instanceof WeakMap)) return response;
  const payload = response?.data;
  const markets = Array.isArray(payload?.snapshot?.markets)
    ? payload.snapshot.markets
    : Array.isArray(payload?.markets) ? payload.markets : null;
  if (markets) tracker.set(markets, requestRevision);
  return response;
};

export const sportsnewDetailRestRevision = (tracker, markets, explicitRevision = null) => (
  Number.isFinite(explicitRevision)
    ? explicitRevision
    : (tracker instanceof WeakMap ? tracker.get(markets) : null) ?? null
);

// Command evidence belongs to active selections in this account/mode/scope,
// not to the public display cache. Keep it in the existing bounded ledger.
export const syncSportsnewCommandScope = (ledger, owner, mode, scope, activeIds) => {
  const previous = ledger.commandScope;
  const sameOwner = previous && previous.owner === owner
    && previous.mode === mode && previous.scope === scope;
  const active = new Set(activeIds.map(String));
  for (const id of ledger.commandIds ?? []) {
    if (sameOwner && active.has(id)) continue;
    ledger.byOddId.delete(id);
    ledger.commandIds.delete(id);
  }
  ledger.commandScope = { owner, mode, scope };
};

const trimOddLedger = ledger => {
  for (const id of ledger.byOddId.keys()) {
    if (ledger.byOddId.size <= MAX_TRACKED_ODDS) break;
    if (!ledger.commandIds?.has(id)) ledger.byOddId.delete(id);
  }
};

export const hasSportsnewCommandEvidence = (ledger, id) => (
  Object.values(ledger.byOddId.get(String(id))?.groups ?? {})
    .some(group => group.origin === 'command')
);

export const recordSportsnewCommandOdd = (ledger, update) => {
  const normalized = normalizeOddPatch({ ...update.patch, oddStateId: update.oddStateId,
    ...(update.priceDecimal != null ? { priceDecimal: update.priceDecimal } : {}) });
  const id = oddId(normalized);
  if (id == null || update.authority !== 'preview_rejection') return normalized;
  const existing = ledger.byOddId.get(id);
  const groups = { ...existing?.groups };
  const revision = ++ledger.revision;
  for (const [name, fields] of Object.entries(SPORTSNEW_ODD_GROUP_FIELDS)) {
    const supplied = fields.filter(field => normalized[field] !== undefined);
    if (!supplied.length) continue;
    groups[name] = { ...sportsnewOddEvidence(update), origin: 'command', order: null,
      revision, authorityOrdinal: update.authorityOrdinal,
      patch: Object.fromEntries(supplied.map(field => [field, normalized[field]])) };
  }
  ledger.byOddId.set(id, { revision, groups, patch: normalized });
  ledger.commandIds ??= new Set();
  ledger.commandIds.add(id);
  trimOddLedger(ledger);
  return withSportsnewDisplayOddEvidence(normalized, groups);
};

// The selected recovery endpoint is a Reader query. Its completion alone
// cannot supersede an exact command. This is the same verdict used by GETs.
export const reconcileSportsnewSelectedReaderOdd = (ledger, update) => {
  const odd = normalizeOddPatch({ ...update.patch, oddStateId: update.oddStateId,
    ...(update.priceDecimal != null ? { priceDecimal: update.priceDecimal } : {}) });
  return overlayRealtimeOdd(odd, ledger.byOddId.get(oddId(odd)), ledger.revision);
};

export const sportsnewRealtimeOddsFromListPatch = (patch = {}) => {
  const rows = [];
  sportsnewListPatchFixtures(patch).forEach(fixture => {
    mapSportsnewFixtureOdds(fixture, odd => { rows.push(odd); return odd; });
  });
  return rows;
};

export const sportsnewFixtureIdsFromListPatch = (patch = {}) => {
  const ids = new Set();
  const add = (value) => {
    const id = value?.fixtureId ?? value?.id ?? value;
    if (id != null) ids.add(String(id));
  };
  sportsnewListPatchFixtures(patch).forEach(add);
  (Array.isArray(patch.ops) ? patch.ops : []).forEach((op) => add(op?.fixture ?? op?.fixtureId));
  add(patch.fixtureId);
  return [...ids];
};

export const sportsnewRealtimeOddsFromFixturePatch = (patch = {}) => {
  const rows = marketOddsRows(patch.marketSnapshot ?? patch.markets);
  if (Array.isArray(patch.odds)) rows.push(...patch.odds);
  if (patch.oddStateId != null) rows.push(patch);
  return rows;
};

export const sportsnewRealtimeMarketsFromFixturePatch = (patch = {}) => (
  Array.isArray(patch.marketSnapshot)
    ? patch.marketSnapshot
    : Array.isArray(patch.markets)
      ? patch.markets
      : []
);

const currentDisplayEntry = (entry, current) => {
  if (!current) return entry;
  let groups = entry?.groups;
  for (const [name, fields] of Object.entries(SPORTSNEW_ODD_GROUP_FIELDS)) {
    if (!fields.some(field => current[field] !== undefined)) continue;
    const evidence = sportsnewDisplayOddGroupEvidence(current)?.[name] ?? sportsnewOddEvidence(current);
    const previous = groups?.[name];
    // A copied cache/DOM row cannot create or remove account-owned command
    // evidence. Active command entries are retained through normal eviction.
    if (evidence.origin === 'command' || previous?.origin === 'command') continue;
    if (previous) {
      const comparableOrder = evidence.order && previous.order
        && evidence.order.room === previous.order.room
        && evidence.order.connection === previous.order.connection && evidence.order.epoch === previous.order.epoch
        && Number.isSafeInteger(evidence.order.sequence)
        && Number.isSafeInteger(previous.order.sequence);
      // A copied row with contradictory ordering cannot supersede the owner.
      if (comparableOrder && evidence.order.sequence < previous.order.sequence) continue;
      const hasRevision = Number.isSafeInteger(evidence.revision) && evidence.revision >= 0;
      const previousHasRevision = Number.isSafeInteger(previous.revision) && previous.revision >= 0;
      // These revisions belong to this Context's event owner. A later display
      // publication can carry an older LOCK; it fences requests, not facts.
      // Equal facts retain the ledger verdict. Only legacy evidence without
      // owner revisions falls back to a comparable connection/room sequence.
      if (hasRevision || previousHasRevision) {
        if (!hasRevision || (previousHasRevision && evidence.revision <= previous.revision)) continue;
      } else if (compareSportsnewOddEvidence(evidence, previous) <= 0) continue;
    }
    const patch = {};
    fields.forEach(field => { if (current[field] !== undefined) patch[field] = current[field]; });
    groups = { ...groups, [name]: { ...evidence, patch } };
  }
  return groups === entry?.groups ? entry : { ...entry, groups };
};

// Called once by the event owner, never inside a React state updater. Returning
// the accepted partial patch makes ledger, list, detail and cache use one verdict.
export const acceptSportsnewDetailRealtimeOdd = (ledger, patch, currentOdd = null, order = null, onAmbiguous = null, options = {}) => {
  if (!patch || !ledger?.byOddId || oddId(patch) == null) return patch;
  const id = oddId(patch);
  const normalized = normalizeOddPatch(patch);
  const accepted = Object.fromEntries(Object.entries(normalized).filter(([, value]) => value !== undefined));
  const incoming = { ...sportsnewOddEvidence(normalized), order, origin: 'ws' };
  const existing = ledger.byOddId.get(id);
  const groups = { ...currentDisplayEntry(existing, currentOdd)?.groups };
  const previousAvailability = groups.availability;
  const incomingHasAvailability = SPORTSNEW_ODD_GROUP_FIELDS.availability.some(field => normalized[field] !== undefined);
  const previousValue = previousAvailability?.patch ?? currentOdd;
  const comparable = order && previousAvailability?.order
    && order.room === previousAvailability.order.room
    && order.connection === previousAvailability.order.connection && order.epoch === previousAvailability.order.epoch
    && Number.isSafeInteger(order.sequence) && Number.isSafeInteger(previousAvailability.order.sequence);
  // A confirmed list successor may replace a public baseline's availability.
  // Real WS/Writer/command evidence keeps its existing cross-room guard.
  const displayBaselineTransition = options.presentationOrderConfirmed === true && !previousAvailability?.origin;
  if (options.requireWriterForUnlock !== false && !displayBaselineTransition && incomingHasAvailability && previousValue && !comparable
    && isEntityLocked(previousValue) !== isEntityLocked(normalized)) {
    if (isEntityLocked(previousValue) && isExplicitOpenEntity(normalized)) {
      onAmbiguous?.(id);
      // There is no shared revision across rooms or REST. Do not optimistically
      // unlock (or reprice) this exact odd; the event owner requests a bounded
      // Writer snapshot. A later comparable LOCK->OPEN is still accepted.
      const held = { ...normalized };
      for (const fields of Object.values(SPORTSNEW_ODD_GROUP_FIELDS)) {
        fields.forEach(field => { delete held[field]; });
      }
      for (const field of ['sourceReceivedAt', 'source_received_at', 'updatedAt', 'updated_at']) delete held[field];
      return held;
    }
  }
  let acceptedGroup = false;
  let changed = false;
  let commandConflict = false;
  for (const [group, fields] of Object.entries(SPORTSNEW_ODD_GROUP_FIELDS)) {
    const supplied = fields.filter(field => normalized[field] !== undefined);
    if (!supplied.length) continue;
    const previous = groups[group];
    if (previous?.origin === 'command'
      && !(group === 'availability' && isEntityLocked(normalized))) {
      commandConflict ||= supplied.some(field => normalized[field] !== previous.patch[field]);
      fields.forEach(field => { delete accepted[field]; });
      continue;
    }
    const fallback = sportsnewDisplayOddGroupEvidence(currentOdd)?.[group]
      ?? (currentOdd ? sportsnewOddEvidence(currentOdd) : null);
    const timeOrder = compareSportsnewOddEvidence(incoming, previous ?? fallback);
    const olderInSameRoom = timeOrder === 0 && order && previous?.order
      && order.room === previous.order.room && order.connection === previous.order.connection && order.epoch === previous.order.epoch
      && Number.isSafeInteger(order.sequence) && Number.isSafeInteger(previous.order.sequence)
      && order.sequence < previous.order.sequence;
    if (timeOrder < 0 || olderInSameRoom) {
      fields.forEach(field => { delete accepted[field]; });
      continue;
    }
    acceptedGroup = true;
    // Parent lock context must survive grouping. Otherwise an OPEN+parent-LOCK
    // payload would be normalized as pure OPEN and a later overlay could unlock.
    const groupInput = group === 'availability'
      ? normalizeAcceptedSportsnewOddAvailability(
        { ...(currentOdd ?? existing?.patch), ...previous?.patch }, normalized)
      : normalized;
    const groupPatch = { ...previous?.patch };
    fields.forEach(field => { if (groupInput[field] !== undefined) groupPatch[field] = groupInput[field]; });
    const same = previous
      && previous.sourceReceivedAt === incoming.sourceReceivedAt
      && previous.updatedAt === incoming.updatedAt
      && (order == null ? previous.order == null : previous.order?.room === order.room
        && previous.order?.connection === order.connection && previous.order?.epoch === order.epoch && previous.order?.sequence === order.sequence)
      && Object.keys(groupPatch).every(field => groupPatch[field] === previous.patch[field]);
    if (!same) {
      groups[group] = { ...incoming, order, revision: ledger.revision + 1, patch: groupPatch,
        // A later LOCK is safe to display but is not proof that an exact
        // command was superseded. Otherwise LOCK then OPEN in one WS room
        // silently removes the command barrier before Writer reconciliation.
        ...(previous?.origin === 'command'
          ? { origin: 'command', authorityOrdinal: previous.authorityOrdinal } : {}),
      };
      changed = true;
    }
  }
  if (!acceptedGroup) {
    for (const field of ['sourceReceivedAt', 'source_received_at', 'updatedAt', 'updated_at']) delete accepted[field];
  }
  if (changed) {
    ledger.revision += 1;
    ledger.byOddId.delete(id);
    ledger.byOddId.set(id, {
      ...incoming, revision: ledger.revision, groups,
      patch: mergeOddPreservingIdentity(existing?.patch, accepted),
    });
  }
  if (commandConflict) onAmbiguous?.(id);
  if (!hasSportsnewCommandEvidence(ledger, id)) ledger.commandIds?.delete(id);
  trimOddLedger(ledger);
  const acceptedEvidence = {};
  for (const [group, fields] of Object.entries(SPORTSNEW_ODD_GROUP_FIELDS)) {
    if (fields.some(field => accepted[field] !== undefined)) acceptedEvidence[group] = groups[group];
  }
  return withSportsnewDisplayOddEvidence(accepted, acceptedEvidence);
};

export const recordSportsnewDetailRealtimeOdds = (ledger, odds = []) => {
  if (!ledger || !(ledger.byOddId instanceof Map)) return ledger?.revision ?? 0;
  (Array.isArray(odds) ? odds : [odds]).forEach(patch => acceptSportsnewDetailRealtimeOdd(ledger, patch));
  return ledger.revision;
};

/** Record authoritative market lifecycle fences. A status-only OPEN is never
 * recorded as an optimistic reopen; an OPEN entry requires active child-odd
 * evidence from the same exact fragment. */
export const recordSportsnewDetailRealtimeMarkets = (ledger, markets = [], oddEvidence = []) => {
  if (!ledger || !(ledger.byMarketKey instanceof Map)) return ledger?.revision ?? 0;

  (Array.isArray(markets) ? markets : [markets]).forEach((market) => {
    const key = marketLedgerKey(market);
    if (key == null) return;
    const normalized = normalizeMarketLifecyclePatch(market);
    const existing = ledger.byMarketKey.get(key);
    const locked = isEntityLocked(normalized);
    const authoritativeOpen = !locked && hasAuthoritativeOpenEvidence(market, oddEvidence);
    if (!locked && !authoritativeOpen) return;

    ledger.revision += 1;
    const entry = {
      revision: ledger.revision,
      sourceReceivedAt: timestamp(normalized.sourceReceivedAt ?? normalized.source_received_at),
      updatedAt: timestamp(normalized.updatedAt ?? normalized.updated_at),
      patch: normalized,
    };
    if (!existing || compareMarketEntries(entry, existing) >= 0) {
      ledger.byMarketKey.delete(key);
      ledger.byMarketKey.set(key, entry);
    }
  });

  while (ledger.byMarketKey.size > MAX_TRACKED_MARKETS) {
    ledger.byMarketKey.delete(ledger.byMarketKey.keys().next().value);
  }
  return ledger.revision;
};

const shouldOverlayRealtime = (restOdd, entry, requestRevision, options = {}) => {
  if (entry.origin === 'command' && options.authority !== 'writer') return true;
  if (options.presentation) {
    const { cursor, room } = options.presentation;
    // Only the same server stream proves that a public baseline includes an
    // earlier WS group. Other rooms/epochs and Writer/command evidence survive.
    if (entry.origin === 'ws' && cursor && entry.order?.room === room
      && entry.order?.epoch === cursor.epoch && Number.isSafeInteger(entry.order?.sequence)) {
      return entry.order.sequence > cursor.sequence;
    }
    return entry.origin != null && entry.origin !== 'rest';
  }
  // WS/command observations and completed Writer reads can supersede a GET
  // that started earlier. A subsequent GET may converge normally.
  return entry.origin !== 'rest'
    && Number.isFinite(requestRevision)
    && Math.max(entry.revision, entry.publicationRevision ?? entry.revision) > requestRevision;
};

// A parent market fence is not an observation of the child selection. Keep its
// lifecycle projection identical for detail display and the effective ledger.
const marketLockAvailability = (entry) => {
  const state = entry.patch?.marketState?.state ?? entry.patch?.state
    ?? entry.patch?.displayState ?? 'LOCKED';
  const reason = entry.patch?.marketState?.reason ?? entry.patch?.reason
    ?? entry.patch?.lockReason ?? 'MARKET_LOCKED';
  return {
    bettable: false, isBettable: false, is_bettable: false,
    suspended: true, isSuspended: true, is_suspended: true, locked: true,
    state, displayState: state, reason, lockReason: reason,
    ...(entry.patch.lockedSince != null ? { lockedSince: entry.patch.lockedSince } : {}),
  };
};

const overlayRealtimeOdd = (restOdd, entry, requestRevision, options = {}, fixtureId = null) => {
  if (!entry) return restOdd;
  let next = restOdd;
  // Compatibility with callers/tests holding an entry created before this change.
  const groups = entry.groups ? Object.entries(entry.groups) : [[null, entry]];
  for (const [name, group] of groups) {
    const missingCommandGroup = name && group.origin === 'command'
      && !SPORTSNEW_ODD_GROUP_FIELDS[name].some(field => restOdd[field] !== undefined);
    if (missingCommandGroup || shouldOverlayRealtime(restOdd, group, requestRevision, options)) {
      next = mergeOddPatchPreservingIdentity(next,
        name ? withSportsnewDisplayOddEvidence(group.patch, { [name]: group }) : group.patch);
    }
  }
  const availability=entry.groups?.availability;
  if(!options.presentation && options.requireWriterForUnlock===true && options.authority!=='writer' && availability && isEntityLocked(availability.patch)
    && isExplicitOpenEntity(next) && !isEntityLocked(next)){
    // Ordinary REST may be served by an older Reader/cache. A completed GET
    // is not itself proof that a known server lock has been released.
    next=mergeOddPatchPreservingIdentity(next,
      withSportsnewDisplayOddEvidence(availability.patch,{availability:{...availability,unverifiedRestHold:true}}));
    options.onAmbiguous?.(oddId(restOdd),fixtureId);
  }
  return next;
};

// A newly accepted REST value is also a display baseline for later WS events.
// Preserve groups which already won the request-boundary overlay; do not turn
// a price-only response into evidence about the availability group.
export const recordSportsnewRestOdds = (ledger, odds, requestRevision, options = {}) => {
  // Completion, not request start, separates this Writer snapshot from older
  // in-flight Reader responses. One boundary per snapshot, not per odd/group.
  const commitRevision = options.authority === 'writer' ? ++ledger.revision : ledger.revision;
  const observed = new Map();
  for (const odd of odds) {
    const normalized = normalizeOddPatch(odd);
    const existing = ledger.byOddId.get(oddId(odd));
    if (oddId(odd) == null) continue;
    const groups = { ...existing?.groups };
    let suppliedGroups = 0;
    let observedGroups = 0;
    for (const [group, fields] of Object.entries(SPORTSNEW_ODD_GROUP_FIELDS)) {
      if (fields.some(field => normalized[field] !== undefined)) suppliedGroups += 1;
      const displayed = sportsnewDisplayOddGroupEvidence(odd)?.[group];
      if (displayed && (displayed.unverifiedRestHold || shouldOverlayRealtime(odd, displayed, requestRevision, options))) {
        groups[group] = { ...displayed,
          patch: Object.fromEntries(fields.filter(field => normalized[field] !== undefined).map(field => [field, normalized[field]])) };
        continue;
      }
      if (existing?.groups?.[group] && shouldOverlayRealtime(odd, existing.groups[group], requestRevision, options)) {
        continue;
      }
      const supplied = fields.filter(field => normalized[field] !== undefined);
      if (!supplied.length) continue;
      const parent = group === 'availability' ? marketEntryFor(ledger, normalized) : null;
      if (parent && parent.revision > requestRevision && isEntityLocked(parent.patch)) {
        // The raw Writer row predates this parent LOCK. Record the effective
        // availability without claiming Writer observed it, or changing an
        // absent group. The original market revision remains its authority;
        // publicationRevision only fences GETs pending when we publish this
        // verdict (including PREMATCH, which permits subsequent Reader reads).
        groups[group] = { ...sportsnewOddEvidence(parent.patch), origin: 'market', order: null,
          revision: parent.revision, publicationRevision: commitRevision,
          patch: marketLockAvailability(parent) };
        continue;
      }
      groups[group] = { ...sportsnewOddEvidence(normalized), origin: options.authority === 'writer' ? 'writer' : 'rest', order: null,
        revision: commitRevision,
        patch: Object.fromEntries(supplied.map(field => [field, normalized[field]])) };
      observedGroups += 1;
    }
    ledger.byOddId.delete(oddId(odd));
    ledger.byOddId.set(oddId(odd), { revision: ledger.revision, groups,
      patch: Object.assign({}, normalized, ...Object.values(groups).map(group => group.patch)) });
    if (!hasSportsnewCommandEvidence(ledger, oddId(odd))) ledger.commandIds?.delete(oddId(odd));
    trimOddLedger(ledger);
    // Only raw groups accepted at this request boundary can reset selected
    // freshness. A newer command/WS overlay is not this response's observation.
    if (suppliedGroups > 0 && suppliedGroups === observedGroups) observed.set(oddId(odd), normalized);
  }
  return observed;
};

// The REST rows own membership, order and market/line identity. Only overlay
// matching existing odds; never resurrect a row or market from the ledger.
export const applySportsnewListRealtimeSince = (fixtures, ledger, requestRevision, currentFixtures = [], options = {}) => {
  if (!Array.isArray(fixtures) || !ledger?.byOddId) return fixtures;
  const currentById = new Map();
  currentFixtures.forEach(fixture => mapSportsnewFixtureOdds(fixture, odd => {
    currentById.set(oddId(odd), odd); return odd;
  }));
  const currentByFixture = new Map(currentFixtures.map(fixture => [String(fixture.fixtureId ?? fixture.id), fixture]));
  let changed = false;
  const next = fixtures.map(fixture => {
    let snapshot = fixture;
    if (options.presentation && fixture.mainOdds) {
      const currentMain = currentByFixture.get(String(fixture.fixtureId ?? fixture.id))?.mainOdds;
      for (const slot of Object.keys(fixture.mainOdds)) {
        const current = currentMain?.[slot];
        if (!current || oddId(current) === oddId(fixture.mainOdds[slot])) continue;
        const entry = currentDisplayEntry(ledger.byOddId.get(oddId(current)), current);
        if (Object.values(entry?.groups ?? {}).some(group => shouldOverlayRealtime(current, group, requestRevision, options))) {
          snapshot = { ...snapshot, mainOdds: { ...snapshot.mainOdds, [slot]: current } };
        }
      }
    }
    const mapped = mapSportsnewFixtureOdds(snapshot, odd => {
      const current = currentById.get(oddId(odd));
      const entry = currentDisplayEntry(ledger.byOddId.get(oddId(odd)), current);
      return overlayRealtimeOdd(odd, entry, requestRevision, options, fixture.fixtureId ?? fixture.id);
    });
    if (mapped !== fixture) changed = true;
    return mapped;
  });
  return changed ? next : fixtures;
};

// React may replay a state updater after unrelated ledger writes or eviction.
// Capture only this completion's LOCK verdicts, never read that mutable ledger
// again from the updater. Membership and price still belong to its input rows.
export const snapshotSportsnewReturnedMarketLocks = (ledger, returnedIds) => {
  const snapshot = { revision: ledger.revision, byOddId: new Map() };
  returnedIds.forEach(id => {
    const entry = ledger.byOddId.get(id), availability = entry?.groups?.availability;
    if (availability?.origin !== 'market' || availability.publicationRevision !== snapshot.revision
      || !isEntityLocked(availability.patch)) return;
    snapshot.byOddId.set(id, {
      patch: Object.fromEntries(['fixtureId', 'marketStateId', 'marketGroupId'].map(key => [key, entry.patch[key]])),
      groups: { availability: { ...availability, patch: { ...availability.patch } } },
    });
  });
  return snapshot;
};

// A list GET can finish before an older recovery and advance only its list
// generation. Do not apply that recovery's page/price/OPEN to the new list.
// A newly reconciled parent LOCK for a returned selection is still safe, as
// long as the Context retains the same session/mode/filter and fixture owner.
export const applySportsnewReturnedMarketLocks = (fixtures, ledger, returnedIds, fixtureId, publicationRevision = ledger.revision) => (
  fixtures.map(fixture => String(fixture.fixtureId ?? fixture.id) !== String(fixtureId) ? fixture
    : mapSportsnewFixtureOdds(fixture, odd => {
      const id = oddId(odd);
      if (!returnedIds.has(id)) return odd;
      const entry = ledger.byOddId.get(id);
      if (['fixtureId', 'marketStateId', 'marketGroupId'].some(key => odd[key] != null
        && entry?.patch?.[key] != null && String(odd[key]) !== String(entry.patch[key]))) return odd;
      const availability = entry?.groups?.availability;
      if (availability?.origin !== 'market' || availability.publicationRevision !== publicationRevision
        || !isEntityLocked(availability.patch)) return odd;
      return mergeOddPreservingIdentity(odd,
        withSportsnewDisplayOddEvidence(availability.patch, { availability }));
    }))
);

export const applySportsnewDetailRealtimeSince = (
  markets,
  ledger,
  requestRevision,
  currentMarkets = [],
  options = {},
) => {
  if (
    !Array.isArray(markets)
    || !ledger
    || !(ledger.byOddId instanceof Map)
    || !(ledger.byMarketKey instanceof Map)
  ) return markets;
  const currentById = new Map(marketOddsRows(currentMarkets).map(odd => [oddId(odd), odd]));
  let changed = false;
  const nextMarkets = markets.map((market) => {
    const marketEntry = marketEntryFor(ledger, market);
    const overlayMarketLock = (
      marketEntry
      && shouldOverlayRealtime(market, marketEntry, requestRevision)
      && isEntityLocked(marketEntry.patch)
    );
    const key = market?.selections ? 'selections' : market?.odds ? 'odds' : null;
    if (!key || !Array.isArray(market[key])) {
      if (!overlayMarketLock) return market;
      changed = true;
      return { ...market, ...marketEntry.patch };
    }

    let marketChanged = overlayMarketLock;
    const nextOdds = market[key].map((restOdd) => {
      const entry = currentDisplayEntry(ledger.byOddId.get(oddId(restOdd)), currentById.get(oddId(restOdd)));
      let nextOdd = overlayRealtimeOdd(restOdd, entry, requestRevision, options);
      if (nextOdd !== restOdd) marketChanged = true;
      if (overlayMarketLock) {
        nextOdd = withSportsnewDisplayOddEvidence({
          ...nextOdd,
          ...marketLockAvailability(marketEntry),
        }, sportsnewDisplayOddGroupEvidence(nextOdd));
      }
      return nextOdd;
    });
    if (!marketChanged) return market;
    changed = true;
    return {
      ...market,
      ...(overlayMarketLock ? marketEntry.patch : {}),
      [key]: nextOdds,
    };
  });
  return changed ? nextMarkets : markets;
};

export const applySportsnewOddPatchToDetailMarkets = (markets, patch) => (
  applySportsnewOddPatchesToMarkets(markets, [patch])
);
